/**
 * The client half of N1's Option B claim mint.
 *
 * The `mintSupabaseRoleClaim` auth trigger runs *asynchronously* after Firebase
 * creates the user, so the vendor's own caveat applies: "the very first ID token
 * received by the Firebase client library in your app will not contain the
 * role: 'authenticated' claim" — you must force a refresh after sign-up.
 *   https://supabase.com/docs/guides/auth/third-party/firebase-auth
 *
 * The same gap exists, once, for every user who was signed in before the
 * backfill ran: a claim added out of band is not in a token already in memory.
 *
 * CONDITIONAL, not unconditional. `getIdTokenResult()` with no argument reads
 * the cached token and costs nothing; only a token that actually lacks the claim
 * pays for a network round trip. An unconditional `getIdToken(true)` would put
 * an extra Identity Toolkit call on the critical path of every page load for
 * every user, forever, and buys nothing once the backfill window closes. This
 * form is self-disarming: when every token carries the claim, it never refreshes
 * again.
 *
 * Nothing in this app reads the claim — it exists for the next app's RLS.
 * Failure here must therefore never block a sign-in; call sites treat it as
 * best-effort.
 */
export const SUPABASE_ROLE_CLAIM = "authenticated";

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Ensure the signed-in user's ID token carries `role: "authenticated"`.
 *
 * @param {import("firebase/auth").User | null} user
 * @param {{ retries?: number, delayMs?: number, sleep?: (ms: number) => Promise<void> }} [options]
 *   `retries` is for the sign-up path, where the trigger may not have written
 *   the claim yet. Zero (the default) means: check the cached token, and force
 *   at most one refresh.
 * @returns {Promise<{ present: boolean, refreshes: number }>} `refreshes` is the
 *   count G10 asks to be recorded — 0 under Option A, >=1 under Option B.
 */
export const ensureSupabaseRoleClaim = async (user, options = {}) => {
  const { retries = 0, delayMs = 500, sleep = defaultSleep } = options;

  if (!user) return { present: false, refreshes: 0 };

  const cached = await user.getIdTokenResult();
  if (cached?.claims?.role === SUPABASE_ROLE_CLAIM) {
    return { present: true, refreshes: 0 };
  }

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (attempt > 0) await sleep(delayMs);
    const refreshed = await user.getIdTokenResult(true);
    if (refreshed?.claims?.role === SUPABASE_ROLE_CLAIM) {
      return { present: true, refreshes: attempt + 1 };
    }
  }

  return { present: false, refreshes: retries + 1 };
};
